import React, { useState } from 'react';
import { getProfileVideos, getAIInsights } from '../services/api';
import { hasReachedLimit, incrementSearchCount } from '../services/searchLimit';
import TikTokEmbed from './TikTokEmbed';
import SearchLimitBanner from './SearchLimitBanner';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  Grid,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import CommentIcon from '@mui/icons-material/Comment';
import PersonIcon from '@mui/icons-material/Person';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const calculateEngagementRate = (video) => {
  if (!video?.stats) return 0;
  const diggCount = video.stats.diggCount || 0;
  const commentCount = video.stats.commentCount || 0;
  const playCount = video.stats.playCount || 0;
  return playCount > 0 ? ((diggCount + commentCount) / playCount) * 100 : 0;
};

const predictEngagementRate = (currentRate, posts) => {
  // Simple prediction model: slight increase for consistent posting
  const baseIncrease = 0.05; // 5% base increase per post
  const diminishingFactor = 0.95; // Each additional post has slightly less impact
  
  const predictedIncrease = posts * baseIncrease * Math.pow(diminishingFactor, posts - 1);
  return currentRate * (1 + predictedIncrease);
};

const ProfileAnalysis = () => {
  const [username, setUsername] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [insights, setInsights] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username) {
      setError('Please enter a username');
      return;
    }

    if (hasReachedLimit()) {
      setError("You've reached the free search limit. Join the waitlist for unlimited access!");
      return;
    }

    // Clear previous data when starting a new search
    setVideos([]);
    setInsights(null);
    setError('');

    setLoading(true);
    try {
      console.log('Fetching profile videos for:', username);
      const data = await getProfileVideos(username, true); // Force refresh
      console.log('Received video data:', data);
      console.log('First video stats:', data.videos[0]?.stats);
      setVideos(data.videos);
      
      // Get AI insights for the videos
      try {
        const insightsData = await getAIInsights(data.videos, username);
        if (insightsData?.insights) {
          setInsights(insightsData.insights);
        }
      } catch (insightError) {
        console.error('Error getting insights:', insightError);
        // Don't fail the whole component if insights fail
      }

      incrementSearchCount();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch profile videos');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          borderRadius: 2,
          p: 3
        }}>
          <Avatar sx={{ 
            width: 56, 
            height: 56,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
          }}>
            <PersonIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Profile Analysis
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Analyze engagement patterns and predict future performance
            </Typography>
          </Box>
        </Box>

        <SearchLimitBanner />

        {/* Search Form */}
        <Card sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
          borderRadius: 3,
        }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ position: 'relative', mb: 3 }}>
              <TextField
                fullWidth
                label="TikTok Username"
                placeholder="Enter username without @"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
                    }
                  }
                }}
              />
              <Tooltip
                title="Enter a TikTok username to analyze their profile performance"
                placement="top"
                arrow
              >
                <HelpOutlineIcon 
                  sx={{ 
                    position: 'absolute',
                    right: { xs: 8, sm: 12 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0.6,
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || hasReachedLimit()}
              fullWidth
              sx={{
                height: 48,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                <span>{loading ? 'Analyzing...' : 'Analyze Profile'}</span>
              </Box>
            </Button>
          </Box>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && videos.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Enter a TikTok username above to analyze their profile
            </Typography>
          </Box>
        )}

        {videos.length > 0 && (
          <>
            {/* AI Insights */}
            {insights && (
              <Paper sx={{ 
                p: 3, 
                mb: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                color: 'white',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <PersonIcon sx={{ fontSize: 24, color: 'white' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                    AI Content Analysis
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'rgba(255, 255, 255, 0.9)' }}>
                  {insights}
                </Typography>
              </Paper>
            )}

            {/* Engagement Analysis */}
            <Paper sx={{ 
              p: 3, 
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <PersonIcon sx={{ 
                  fontSize: 24,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  AI-Powered Engagement Analysis
                </Typography>
              </Box>
              {(() => {
                const recentVideos = videos.slice(0, 3);
                const avgEngagementRate = recentVideos.reduce((acc, video) => 
                  acc + calculateEngagementRate(video), 0) / recentVideos.length;
                
                return (
                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ 
                        p: 3, 
                        height: '100%',
                        background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                        color: 'white'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                          <TrendingUpIcon sx={{ color: 'white' }} />
                          <Typography variant="h6" sx={{ color: 'white' }}>Current Performance</Typography>
                        </Box>
                        <Typography variant="h4" sx={{ mb: 1, color: 'white' }}>
                          {avgEngagementRate.toFixed(2)}%
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Average engagement rate across recent videos
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ 
                        p: 3, 
                        height: '100%',
                        background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                        color: 'white'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                          <PersonIcon sx={{ color: 'white' }} />
                          <Typography variant="h6" sx={{ color: 'white' }}>AI Predictions</Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                            Predicted engagement with:
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'white' }}>
                            3 posts: {predictEngagementRate(avgEngagementRate, 3).toFixed(2)}%
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'white' }}>
                            10 posts: {predictEngagementRate(avgEngagementRate, 10).toFixed(2)}%
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'white' }}>
                            20 posts: {predictEngagementRate(avgEngagementRate, 20).toFixed(2)}%
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>
                );
              })()}
            </Paper>

            {/* Recent Videos */}
            <Paper sx={{ 
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <PersonIcon sx={{ 
                  fontSize: 24,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Recent Videos Performance
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {videos.map((video) => (
                  <Grid item xs={12} sm={6} lg={4} xl={4} key={video.id}>
                    <Card sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      width: '100%'
                    }}>
                      <Box sx={{ 
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        flex: 1
                      }}>
                        <Box sx={{ width: '100%', overflow: 'hidden' }}>
                          <TikTokEmbed url={video.videoUrl} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {video.description}
                        </Typography>

                        {/* Metrics with new flexible layout */}
                        <Box sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          justifyContent: 'space-between',
                          mb: 3
                        }}>
                          {/* Likes */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <FavoriteIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                color: '#ef4444',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.diggCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Likes
                            </Typography>
                          </Box>

                          {/* Comments */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <CommentIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.commentCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Comments
                            </Typography>
                          </Box>

                          {/* Views */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <VisibilityIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.playCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Views
                            </Typography>
                          </Box>

                          {/* Shares */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <ShareIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.shareCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Shares
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ 
                          p: 3, 
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                          color: 'white',
                          mb: 2
                        }}>
                          <Typography variant="subtitle2">
                            Engagement Rate: {calculateEngagementRate(video).toFixed(2)}%
                          </Typography>
                        </Box>

                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 'auto'
                        }}>
                          <MusicNoteIcon sx={{ 
                            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            flexShrink: 0
                          }} />
                          <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="subtitle2" sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {video.musicMeta?.musicName || 'Original Sound'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block'
                            }}>
                              {video.musicMeta?.musicAuthor || video.author.name}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </>
        )}
      </Box>
    </Container>
  );
};

export default ProfileAnalysis;
